import { useMemo, useState, useCallback } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Separator,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@design-system';
import {
  FormInput,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { getErrorMessage } from '../utils/get-error-message';
import type { PreRegisteredTeamRelationship } from '../api/api';
import {
  useAddPreRegisteredTeamRelationship,
  useAddPreRegisteredTeamRelationshipsBulk,
  useDeletePreRegisteredTeamRelationship,
  usePreRegisteredTeamRelationshipsList,
} from '../hooks/use-pre-registered-team-relationships';

const ROWS_PER_PAGE = 20;

const addRelationshipSchema = z.object({
  supervisorEmail: z.string().email('A valid supervisor email is required'),
  subordinateEmail: z.string().email('A valid subordinate email is required'),
}).refine(data => data.supervisorEmail.toLowerCase() !== data.subordinateEmail.toLowerCase(), {
  message: 'Supervisor and subordinate emails must be different',
  path: ['subordinateEmail'],
});

const bulkRelationshipSchema = z.object({
  relationships: z.string().min(1, 'At least one relationship is required'),
});

export function PreRegisteredTeamRelationshipsForm() {
  return (
    <div className="space-y-6">
      <AddSection />
      <Separator />
      <RelationshipsTable />
    </div>
  );
}

function AddSection() {
  const [bulkMode, setBulkMode] = useState(false);

  return (
    <Card className="border shadow-sm">
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Add Relationship</h3>
          <div className="flex items-center gap-2">
            <Label htmlFor="bulk-toggle" className="text-muted-foreground text-sm">
              Bulk Add
            </Label>
            <Switch
              id="bulk-toggle"
              checked={bulkMode}
              onCheckedChange={setBulkMode}
            />
          </div>
        </div>
        {bulkMode ? <BulkAddForm /> : <SingleAddForm />}
      </CardContent>
    </Card>
  );
}

function SingleAddForm() {
  const [enabledClientAccess, setEnabledClientAccess] = useState(true);
  const [createReversed, setCreateReversed] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof addRelationshipSchema>>({
    resolver: zodResolver(addRelationshipSchema),
  });

  const { addPreRegisteredTeamRelationship, isPending, isError, error } =
    useAddPreRegisteredTeamRelationship();

  const onSubmit = async (data: z.infer<typeof addRelationshipSchema>) => {
    try {
      await addPreRegisteredTeamRelationship({
        supervisorEmail: data.supervisorEmail,
        subordinateEmail: data.subordinateEmail,
        enabledClientAccess,
      });

      if (createReversed) {
        await addPreRegisteredTeamRelationship({
          supervisorEmail: data.subordinateEmail,
          subordinateEmail: data.supervisorEmail,
          enabledClientAccess,
        });
      }

      reset();
      setEnabledClientAccess(true);
      setCreateReversed(false);
    } catch {
      // Error is captured by the mutation's isError state
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          label="Supervisor Email"
          id="supervisor-email"
          type="email"
          placeholder="supervisor@example.com"
          error={errors.supervisorEmail}
          register={register('supervisorEmail')}
          required
        />
        <FormInput
          label="Subordinate Email"
          id="subordinate-email"
          type="email"
          placeholder="subordinate@example.com"
          error={errors.subordinateEmail}
          register={register('subordinateEmail')}
          required
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enabled-client-access"
              checked={enabledClientAccess}
              onCheckedChange={(checked) => setEnabledClientAccess(checked === true)}
            />
            <Label htmlFor="enabled-client-access" className="text-sm">
              Enable Client Access
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="create-reversed"
              checked={createReversed}
              onCheckedChange={(checked) => setCreateReversed(checked === true)}
            />
            <Label htmlFor="create-reversed" className="text-sm">
              Also create reversed relationship (bidirectional)
            </Label>
          </div>
        </div>

        <SubmitButton
          label="Add Relationship"
          isLoading={isPending}
        />
      </div>

      {isError && (
        <FormErrorAlert
          mainErrorMessage={getErrorMessage(error, 'Failed to add relationship')}
        />
      )}
    </form>
  );
}

function BulkAddForm() {
  const [enabledClientAccess, setEnabledClientAccess] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof bulkRelationshipSchema>>({
    resolver: zodResolver(bulkRelationshipSchema),
  });

  const { addBulk, isPending, isError, error } = useAddPreRegisteredTeamRelationshipsBulk();

  const onSubmit = (data: z.infer<typeof bulkRelationshipSchema>) => {
    const lines = data.relationships
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const relationships = lines.map(line => {
      const [supervisorEmail, subordinateEmail] = line.split(',').map(e => e.trim());
      return { supervisorEmail, subordinateEmail, enabledClientAccess };
    }).filter(r => r.supervisorEmail && r.subordinateEmail);

    if (relationships.length === 0) return;

    addBulk(
      { relationships },
      {
        onSuccess: () => {
          reset();
          setEnabledClientAccess(true);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-1.5">
        <label htmlFor="bulk-relationships" className="text-sm font-medium">
          Relationships (one per line)
        </label>
        <textarea
          id="bulk-relationships"
          className="min-h-30 bg-background placeholder:text-muted-foreground focus:ring-primary flex w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
          placeholder={"supervisor@example.com, subordinate@example.com\nsupervisor2@example.com, subordinate2@example.com"}
          {...register('relationships')}
        />
        {errors.relationships && <p className="text-destructive text-xs">{errors.relationships.message}</p>}
        <p className="text-muted-foreground text-xs">
          Each line: supervisor email, subordinate email (comma-separated). Duplicates will be skipped.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="bulk-client-access"
            checked={enabledClientAccess}
            onCheckedChange={(checked) => setEnabledClientAccess(checked === true)}
          />
          <Label htmlFor="bulk-client-access" className="text-sm">
            Enable Client Access (applies to all)
          </Label>
        </div>

        <SubmitButton
          label="Add Relationships"
          isLoading={isPending}
        />
      </div>

      {isError && (
        <FormErrorAlert
          mainErrorMessage={getErrorMessage(error, 'Failed to add relationships')}
        />
      )}
    </form>
  );
}

function RelationshipsTable() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);

  const { data: allRelationships, isLoading, isFetching, refetch } = usePreRegisteredTeamRelationshipsList();
  const { deletePreRegisteredTeamRelationship, isPending: isDeleting } =
    useDeletePreRegisteredTeamRelationship();

  const filtered = useMemo(() => {
    if (!allRelationships) return [];
    if (!searchQuery.trim()) return allRelationships;
    const q = searchQuery.toLowerCase();
    return allRelationships.filter(
      r => r.supervisorEmail.toLowerCase().includes(q) || r.subordinateEmail.toLowerCase().includes(q),
    );
  }, [allRelationships, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const pageRows = filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Pre-Registered Relationships</h3>
          {allRelationships && (
            <p className="text-muted-foreground text-sm">
              {filtered.length} {filtered.length === 1 ? 'record' : 'records'}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            className="max-w-xs"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card className="border shadow-sm">
        {isLoading ? (
          <p className="text-muted-foreground py-12 text-center text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center text-sm">
            {searchQuery ? 'No matching relationships found.' : 'No pre-registered relationships yet.'}
          </p>
        ) : (
          <Table>
            <TableHeader className="bg-muted/50 [&_tr]:border-b">
              <TableRow>
                <TableHead className="font-semibold">Supervisor</TableHead>
                <TableHead className="font-semibold">Subordinate</TableHead>
                <TableHead className="font-semibold">Client Access</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Created By</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((rel) => (
                <RelationshipRow
                  key={rel.id}
                  relationship={rel}
                  onDelete={(id) => deletePreRegisteredTeamRelationship(id)}
                  isDeleting={isDeleting}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RelationshipRow({
  relationship,
  onDelete,
  isDeleting,
}: {
  relationship: PreRegisteredTeamRelationship;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{relationship.supervisorEmail}</TableCell>
      <TableCell className="font-medium">{relationship.subordinateEmail}</TableCell>
      <TableCell>
        <Badge variant={relationship.enabledClientAccess ? 'default' : 'outline'}>
          {relationship.enabledClientAccess ? 'Yes' : 'No'}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={relationship.status === 'established' ? 'success' : 'outline'}>
          {relationship.status === 'established' ? 'Established' : 'Pending'}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{relationship.createdBy}</TableCell>
      <TableCell className="text-muted-foreground">
        {new Date(relationship.createdAt).toLocaleDateString()}
      </TableCell>
      <TableCell>
        {relationship.status === 'pending' && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(relationship.id)}
            disabled={isDeleting}
          >
            Delete
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
